import { Mail, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DialogContentSafeArea } from '@/components/ui/dialog-safe-area';
import { Button } from '@/components/ui/button';
import { KontentinoPromoCard } from '../KontentinoPromoCard';

interface EmailDialogProps {
  open: boolean;
  emailAddress: string;
  isSendingEmail: boolean;
  emailSendError: string | null;
  onClose: () => void;
  onEmailChange: (email: string) => void;
  onConfirmSend: () => void;
}

export function EmailDialog({
  open,
  emailAddress,
  isSendingEmail,
  emailSendError,
  onClose,
  onEmailChange,
  onConfirmSend,
}: EmailDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContentSafeArea className="sm:max-w-lg max-h-[85vh] overflow-y-auto [scrollbar-gutter:stable]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Send for Approval
          </DialogTitle>
          <DialogDescription>
            Send the calendar PDF to stakeholders for review
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email-input" className="text-sm font-medium">
              Email Address
            </label>
            <input
              id="email-input"
              type="email"
              placeholder="recipient@example.com"
              value={emailAddress}
              onChange={(e) => onEmailChange(e.target.value)}
              className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSendingEmail}
            />
          </div>

          {emailSendError && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              {emailSendError}
            </div>
          )}

          <KontentinoPromoCard className="mt-6" />
        </div>

        <div className="flex gap-2 justify-end pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSendingEmail}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirmSend}
            disabled={isSendingEmail || !emailAddress || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress)}
          >
            {isSendingEmail ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Send Email
              </>
            )}
          </Button>
        </div>
      </DialogContentSafeArea>
    </Dialog>
  );
}
